import React from "react";
import axios from "axios";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Tooltip from "@mui/material/Tooltip";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDBadge from "components/MDBadge";
import MDPagination from "components/MDPagination";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";

function Subscriptions() {
  const currentUserRole = "admin";
  const [rowsData, setRowsData] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [page, setPage] = React.useState(1);
  const [limit] = React.useState(10);
  const [totalPages, setTotalPages] = React.useState(1);
  const [totalSubscriptionsCount, setTotalSubscriptionsCount] = React.useState(0);
  const [reloadKey, setReloadKey] = React.useState(0);
  const [adjustDialog, setAdjustDialog] = React.useState({ open: false, id: "", days: "30" });
  const [editDialog, setEditDialog] = React.useState({
    open: false,
    id: "",
    nextBilling: "",
    durationAction: "none",
    paused: false,
    autoConvert: false,
  });
  const [filterStatus, setFilterStatus] = React.useState("all");
  const [filterTrial, setFilterTrial] = React.useState("all");
  const token =
    localStorage.getItem("dsf_token") ||
    localStorage.getItem("token") ||
    localStorage.getItem("accessToken") ||
    "";
  const apiBaseUrl = (
    process.env.REACT_APP_API_BASE_URL || "https://dsf-api.classicprogrammers.com/api/v1/admin"
  ).replace(/\/+$/, "");

  const formatDate = React.useCallback((value) => {
    if (!value) return "—";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "—";
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(date);
  }, []);

  const toInputDate = React.useCallback((value) => {
    if (!value || value === "—") return "";
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toISOString().slice(0, 10);
  }, []);

  const toApiDate = React.useCallback(
    (value, fallbackValue) => {
      const parsed = toInputDate(value);
      if (parsed) return parsed;
      const fallback = toInputDate(fallbackValue);
      return fallback || "";
    },
    [toInputDate]
  );

  const toIsoStartOfDay = React.useCallback(
    (value) => {
      const normalized = toInputDate(value);
      if (!normalized) return "";
      const date = new Date(`${normalized}T00:00:00.000Z`);
      if (Number.isNaN(date.getTime())) return "";
      return date.toISOString();
    },
    [toInputDate]
  );

  React.useEffect(() => {
    setPage(1);
  }, [filterStatus, filterTrial]);

  React.useEffect(() => {
    const fetchSubscriptions = async () => {
      setIsLoading(true);
      const query = new URLSearchParams({
        page: String(page),
        limit: String(limit),
        search: "",
      });
      if (filterStatus !== "all") query.set("status", filterStatus);
      if (filterTrial !== "all") query.set("trial", filterTrial);
      const endpoint = `/subscriptions?${query.toString()}`;
      const requestUrls = [];
      if (apiBaseUrl) requestUrls.push(`${apiBaseUrl}${endpoint}`);

      try {
        let response;
        for (const url of requestUrls) {
          try {
            response = await axios.get(url, {
              headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            break;
          } catch (_requestError) {
            // Try next candidate.
          }
        }

        const payload = response?.data?.data || response?.data || {};
        const list = Array.isArray(payload.subscriptions)
          ? payload.subscriptions
          : Array.isArray(payload.docs)
          ? payload.docs
          : Array.isArray(payload.items)
          ? payload.items
          : [];
        const pagination = payload.pagination || payload.meta || {};
        const resolvedTotal =
          Number(
            pagination.total ||
              pagination.totalSubscriptions ||
              payload.total ||
              payload.count ||
              list.length
          ) || 0;
        const resolvedTotalPages =
          Number(
            pagination.totalPages ||
              payload.totalPages ||
              (resolvedTotal > 0 ? Math.ceil(resolvedTotal / limit) : 1)
          ) || 1;

        const normalized = list.map((item, idx) => ({
          id: item?._id || item?.id || `${page}-${idx}`,
          user: item?.user?.name || item?.userName || item?.name || "User",
          plan: item?.plan || item?.planName || "Monthly",
          status: String(item?.status || "active").toLowerCase(),
          trial: item?.trial || item?.trialStatus || "paid",
          nextBilling: formatDate(item?.nextBillingDate || item?.nextBilling),
          billingIssue: item?.billingIssue || item?.billingError || "",
          paymentMethod: item?.paymentMethod || "—",
          durationDays: Number(item?.durationDays || item?.duration || 30),
          isPaused:
            typeof item?.isPaused === "boolean"
              ? item.isPaused
              : ["paused", "cancelled", "canceled", "inactive"].includes(
                  String(item?.status || "").toLowerCase()
                ),
          autoConvert: Boolean(item?.autoConvert ?? item?.autoConvertTrialToPaid),
        }));

        setRowsData(normalized);
        setTotalSubscriptionsCount(resolvedTotal);
        setTotalPages(resolvedTotalPages);
      } catch (_error) {
        setRowsData([]);
        setTotalSubscriptionsCount(0);
        setTotalPages(1);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSubscriptions();
  }, [apiBaseUrl, filterStatus, filterTrial, formatDate, limit, page, reloadKey, token]);

  const [actionAnchorEl, setActionAnchorEl] = React.useState(null);
  const [selectedActionRow, setSelectedActionRow] = React.useState(null);
  const [billingAnchorEl, setBillingAnchorEl] = React.useState(null);
  const [selectedBillingRow, setSelectedBillingRow] = React.useState(null);

  const columns = React.useMemo(
    () => [
      { Header: "user", accessor: "user", align: "left" },
      { Header: "plan", accessor: "plan", align: "left" },
      { Header: "status", accessor: "status", align: "center" },
      { Header: "trial", accessor: "trial", align: "center" },
      { Header: "billing issue", accessor: "billingIssue", align: "center" },
      { Header: "next billing", accessor: "nextBilling", align: "center" },
      { Header: "action", accessor: "action", align: "center" },
    ],
    []
  );

  const statusBadge = (status) => {
    if (status === "active")
      return <MDBadge badgeContent="active" color="success" variant="gradient" size="sm" />;
    if (status === "past_due")
      return <MDBadge badgeContent="past due" color="warning" variant="gradient" size="sm" />;
    if (status === "canceled")
      return <MDBadge badgeContent="canceled" color="dark" variant="gradient" size="sm" />;
    if (status === "paused")
      return <MDBadge badgeContent="paused" color="secondary" variant="gradient" size="sm" />;
    if (status === "trial")
      return <MDBadge badgeContent="trial" color="info" variant="gradient" size="sm" />;
    return <MDBadge badgeContent={status} color="info" variant="gradient" size="sm" />;
  };

  const trialBadge = (trial) => {
    if (trial === "trial")
      return <MDBadge badgeContent="trial" color="info" variant="gradient" size="sm" />;
    if (trial === "trial-to-paid")
      return <MDBadge badgeContent="trial-to-paid" color="warning" variant="gradient" size="sm" />;
    return <MDBadge badgeContent="paid" color="success" variant="gradient" size="sm" />;
  };

  const hasBillingIssue = React.useCallback((value) => {
    const normalizedValue = String(value || "")
      .trim()
      .toLowerCase();
    return Boolean(normalizedValue && normalizedValue !== "no_issue" && normalizedValue !== "none");
  }, []);

  const getBillingIssueBadgeColor = React.useCallback(
    (value) => (hasBillingIssue(value) ? "error" : "info"),
    [hasBillingIssue]
  );

  const updateSubscription = (id, updater) => {
    setRowsData((prev) =>
      prev.map((item) => (item.id === id ? { ...item, ...updater(item) } : item))
    );
  };

  const patchSubscriptionEndpoint = React.useCallback(
    async (subscriptionId, endpointSuffix = "", body = {}) => {
      if (!subscriptionId) return;
      const endpoint = `/subscriptions/${subscriptionId}${endpointSuffix}`;
      const requestUrls = [];
      if (apiBaseUrl) requestUrls.push(`${apiBaseUrl}${endpoint}`);

      let lastError;
      for (const url of requestUrls) {
        try {
          const response = await axios.patch(url, body, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          });
          return response?.data;
        } catch (_requestError) {
          lastError = _requestError;
          // Try next candidate.
        }
      }
      throw lastError || new Error("Subscription request failed");
    },
    [apiBaseUrl, token]
  );

  const summary = React.useMemo(
    () => ({
      total: rowsData.length,
      active: rowsData.filter((r) => r.status === "active").length,
      issues: rowsData.filter((r) => r.status === "past_due" || hasBillingIssue(r.billingIssue))
        .length,
      trial: rowsData.filter((r) => r.trial === "trial" || r.trial === "trial-to-paid").length,
    }),
    [hasBillingIssue, rowsData]
  );

  const summaryCards = [
    { label: "Total Subscriptions", value: summary.total, color: "#344767", icon: "groups" },
    { label: "Active", value: summary.active, color: "#4caf50", icon: "task_alt" },
    { label: "Billing Issues", value: summary.issues, color: "#fb8c00", icon: "error_outline" },
    { label: "In Trial / Conversion", value: summary.trial, color: "#1a73e8", icon: "autorenew" },
  ];

  const rows = React.useMemo(
    () =>
      rowsData.map((r) => ({
        user: (
          <MDTypography variant="button" fontWeight="medium" color="text">
            {r.user}
          </MDTypography>
        ),
        plan: (
          <MDTypography variant="caption" color="text">
            {r.plan}
          </MDTypography>
        ),
        status: (
          <MDBox display="flex" justifyContent="center">
            {statusBadge(r.status)}
          </MDBox>
        ),
        trial: (
          <MDBox
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            gap={0.75}
          >
            {trialBadge(r.trial)}
          </MDBox>
        ),
        billingIssue: (
          <MDBox display="flex" justifyContent="center">
            {r.billingIssue ? (
              <MDBox display="flex" alignItems="center">
                <MDBadge
                  badgeContent={r.billingIssue}
                  color={getBillingIssueBadgeColor(r.billingIssue)}
                  variant="contained"
                  size="sm"
                />
              </MDBox>
            ) : (
              <MDTypography variant="caption" color="text">
                No issue
              </MDTypography>
            )}
          </MDBox>
        ),
        nextBilling: (
          <MDBox textAlign="center">
            <MDTypography variant="caption" color="text" display="block">
              {r.nextBilling}
            </MDTypography>
            <MDTypography variant="caption" color="text" display="block">
              {r.durationDays} days
            </MDTypography>
          </MDBox>
        ),
        action: (
          <MDBox display="flex" justifyContent="center">
            <IconButton
              size="small"
              sx={{ color: "#344767" }}
              onClick={(event) => {
                setActionAnchorEl(event.currentTarget);
                setSelectedActionRow(r);
              }}
            >
              <Icon fontSize="small">more_vert</Icon>
            </IconButton>
          </MDBox>
        ),
      })),
    [getBillingIssueBadgeColor, rowsData]
  );

  const handleConfirmAdjust = () => {
    const days = Number(adjustDialog.days);
    if (!adjustDialog.id || Number.isNaN(days) || days <= 0) return;

    const nextDate = new Date();
    nextDate.setDate(nextDate.getDate() + days);
    const formatted = nextDate.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

    updateSubscription(adjustDialog.id, () => ({ nextBilling: formatted, status: "active" }));
    setAdjustDialog({ open: false, id: "", days: "30" });
  };

  const handleSaveEdit = async () => {
    if (!editDialog.id) return;
    if (!window.confirm("Apply subscription changes?")) return;

    const selected = rowsData.find((row) => row.id === editDialog.id);
    if (!selected) return;

    const payload = {};
    const expiryDateIso = toIsoStartOfDay(editDialog.nextBilling || selected.nextBilling);
    if (expiryDateIso) payload.expiryDate = expiryDateIso;
    if (editDialog.durationAction === "extend_7") payload.extendByDays = 7;
    if (editDialog.durationAction === "extend_30") payload.extendByDays = 30;
    payload.isPaused = Boolean(editDialog.paused);
    payload.autoConvertTrialToPaid = Boolean(editDialog.autoConvert);

    try {
      const apiResponse = await patchSubscriptionEndpoint(editDialog.id, "/edit", payload);
      setReloadKey((prev) => prev + 1);
      toast.success(apiResponse?.message || "Subscription updated.");
      setEditDialog({
        open: false,
        id: "",
        nextBilling: "",
        durationAction: "none",
        paused: false,
        autoConvert: false,
      });
    } catch (_error) {
      toast.error("Failed to update subscription.");
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={3}>
          {summaryCards.map((card) => (
            <Grid item xs={12} md={3} key={card.label}>
              <Card
                sx={{
                  boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
                  borderRadius: "0.75rem",
                  overflow: "hidden",
                }}
              >
                <MDBox
                  p={2.2}
                  pr={2.6}
                  minHeight="84px"
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <MDBox minWidth={0}>
                    <MDTypography variant="caption" color="text">
                      {card.label}
                    </MDTypography>
                    <MDTypography variant="h5" fontWeight="bold" color="dark">
                      {card.value}
                    </MDTypography>
                  </MDBox>
                  <MDBox
                    width="2.2rem"
                    height="2.2rem"
                    minWidth="2.2rem"
                    borderRadius="lg"
                    flexShrink={0}
                    sx={{
                      background: `${card.color}1A`,
                      color: card.color,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Icon
                      fontSize="small"
                      sx={{ display: "block", lineHeight: 1, transform: "translate(0, 0)" }}
                    >
                      {card.icon}
                    </Icon>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
          ))}
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={0}
                py={3}
                px={2}
                mb={2}
                variant="contained"
                borderRadius="lg"
                coloredShadow="none"
                sx={{ background: "rgba(229, 10, 70, 1)" }}
              >
                <MDTypography variant="h6" color="white">
                  Subscription Management
                </MDTypography>
              </MDBox>
              <MDBox p={3}>
                <MDBox mb={2}>
                  <MDTypography variant="button" color="text">
                    Track statuses, resolve billing issues, manually adjust periods, and convert
                    trials to paid plans from one place.
                  </MDTypography>
                </MDBox>
                <MDBox
                  p={2}
                  mb={2}
                  borderRadius="lg"
                  sx={{
                    backgroundColor: "rgba(52, 71, 103, 0.04)",
                    border: "1px solid rgba(52,71,103,0.1)",
                  }}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        select
                        fullWidth
                        variant="outlined"
                        label="Filter by status"
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{
                          backgroundColor: "#fff",
                          "& .MuiOutlinedInput-root": { minHeight: "44px" },
                        }}
                      >
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="active">Active</MenuItem>
                        <MenuItem value="past_due">Past Due</MenuItem>
                        <MenuItem value="canceled">Canceled</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        select
                        fullWidth
                        variant="outlined"
                        label="Filter by trial"
                        value={filterTrial}
                        onChange={(e) => setFilterTrial(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{
                          backgroundColor: "#fff",
                          "& .MuiOutlinedInput-root": { minHeight: "44px" },
                        }}
                      >
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="trial">Trial</MenuItem>
                        <MenuItem value="trial-to-paid">Trial-to-paid</MenuItem>
                        <MenuItem value="paid">Paid</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={4} display="flex" alignItems="center">
                      <MDTypography variant="button" color="text">
                        Showing {rowsData.length} of {totalSubscriptionsCount} subscriptions
                      </MDTypography>
                    </Grid>
                  </Grid>
                </MDBox>
                <DataTable
                  table={{ columns, rows }}
                  isSorted={false}
                  entriesPerPage={false}
                  showTotalEntries={false}
                  noEndBorder
                />
                {isLoading ? (
                  <MDBox py={2} display="flex" alignItems="center" justifyContent="center" gap={1}>
                    <CircularProgress size={18} sx={{ color: "rgba(229, 10, 70, 1)" }} />
                    <MDTypography variant="caption" color="text">
                      Loading subscriptions...
                    </MDTypography>
                  </MDBox>
                ) : (
                  <MDBox
                    pt={2.5}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    flexWrap="wrap"
                    gap={1}
                  >
                    <MDBox display="flex" flexDirection="column">
                      <MDTypography variant="caption" color="text" fontWeight="medium">
                        Page {page} of {totalPages} ({totalSubscriptionsCount} subscriptions)
                      </MDTypography>
                      <MDTypography variant="caption" color="text">
                        Showing up to {limit} subscriptions per page
                      </MDTypography>
                    </MDBox>
                    <MDPagination
                      variant="gradient"
                      color="dark"
                      sx={{
                        "& .MuiPaginationItem-root": {
                          color: "rgba(229, 10, 70, 1)",
                          borderColor: "rgba(229, 10, 70, 0.35)",
                        },
                        "& .MuiPaginationItem-root.Mui-selected": {
                          backgroundColor: "rgba(229, 10, 70, 1) !important",
                          color: "#fff !important",
                        },
                      }}
                    >
                      <MDPagination item disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                        <span>{"<"}</span>
                      </MDPagination>
                      {Array.from({ length: totalPages }, (_, index) => index + 1)
                        .slice(Math.max(0, page - 3), Math.max(0, page - 3) + 5)
                        .map((pageNumber) => (
                          <MDPagination
                            key={pageNumber}
                            item
                            active={pageNumber === page}
                            onClick={() => setPage(pageNumber)}
                          >
                            {pageNumber}
                          </MDPagination>
                        ))}
                      <MDPagination
                        item
                        disabled={page >= totalPages}
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      >
                        <span>{">"}</span>
                      </MDPagination>
                    </MDPagination>
                  </MDBox>
                )}
                <Menu
                  id="subscription-row-actions"
                  anchorEl={actionAnchorEl}
                  open={Boolean(actionAnchorEl)}
                  onClose={() => {
                    setActionAnchorEl(null);
                    setSelectedActionRow(null);
                  }}
                  anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                  transformOrigin={{ vertical: "top", horizontal: "center" }}
                >
                  <MenuItem
                    onClick={() => {
                      if (!selectedActionRow) return;
                      setEditDialog({
                        open: true,
                        id: selectedActionRow.id,
                        nextBilling: toInputDate(selectedActionRow.nextBilling),
                        durationAction: "none",
                        paused: Boolean(selectedActionRow.isPaused),
                        autoConvert: Boolean(selectedActionRow.autoConvert),
                      });
                      setActionAnchorEl(null);
                      setSelectedActionRow(null);
                    }}
                  >
                    Edit subscription
                  </MenuItem>
                  <MenuItem
                    onClick={async () => {
                      if (!selectedActionRow) return;
                      const isInactiveStatus = [
                        "canceled",
                        "cancelled",
                        "paused",
                        "inactive",
                      ].includes(String(selectedActionRow.status || "").toLowerCase());
                      const confirmText = isInactiveStatus
                        ? "Activate this subscription?"
                        : "Cancel this subscription?";
                      if (!window.confirm(confirmText)) return;
                      try {
                        const apiResponse = await patchSubscriptionEndpoint(
                          selectedActionRow.id,
                          "/toggle-status"
                        );
                        setReloadKey((prev) => prev + 1);
                        toast.success(apiResponse?.message || "Subscription status updated.");
                      } catch (_error) {
                        toast.error("Failed to update subscription status.");
                      }
                      setActionAnchorEl(null);
                      setSelectedActionRow(null);
                    }}
                  >
                    {["canceled", "cancelled", "paused", "inactive"].includes(
                      String(selectedActionRow?.status || "").toLowerCase()
                    )
                      ? "Activate"
                      : "Cancel"}
                  </MenuItem>
                </Menu>
                <Menu
                  id="subscription-billing-actions"
                  anchorEl={billingAnchorEl}
                  open={Boolean(billingAnchorEl)}
                  onClose={() => {
                    setBillingAnchorEl(null);
                    setSelectedBillingRow(null);
                  }}
                  anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                  transformOrigin={{ vertical: "top", horizontal: "center" }}
                >
                  <MenuItem
                    onClick={async () => {
                      if (!selectedBillingRow) return;
                      if (!window.confirm("Retry payment for this subscription?")) return;
                      updateSubscription(selectedBillingRow.id, () => ({
                        status: "active",
                        billingIssue: "",
                      }));
                      setBillingAnchorEl(null);
                      setSelectedBillingRow(null);
                    }}
                  >
                    Retry Payment
                  </MenuItem>
                  <MenuItem
                    onClick={async () => {
                      if (!selectedBillingRow) return;
                      if (!window.confirm("Mark payment method as updated?")) return;
                      updateSubscription(selectedBillingRow.id, () => ({
                        paymentMethod: "Updated Card",
                      }));
                      setBillingAnchorEl(null);
                      setSelectedBillingRow(null);
                    }}
                  >
                    Update Payment Method
                  </MenuItem>
                  {currentUserRole === "admin" ? (
                    <MenuItem
                      onClick={async () => {
                        if (!selectedBillingRow) return;
                        if (!window.confirm("Mark billing issue as resolved?")) return;
                        await patchSubscriptionEndpoint(selectedBillingRow.id, "/billing-resolve");
                        setReloadKey((prev) => prev + 1);
                        setBillingAnchorEl(null);
                        setSelectedBillingRow(null);
                      }}
                    >
                      Mark as Resolved
                    </MenuItem>
                  ) : null}
                </Menu>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Dialog
        open={adjustDialog.open}
        onClose={() => setAdjustDialog({ open: false, id: "", days: "30" })}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle
          sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
        >
          Adjust Subscription Period
          <IconButton
            size="small"
            onClick={() => setAdjustDialog({ open: false, id: "", days: "30" })}
            sx={{ color: "#344767" }}
          >
            <Icon fontSize="small">close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Extend by days"
            type="number"
            fullWidth
            value={adjustDialog.days}
            onChange={(e) => setAdjustDialog((prev) => ({ ...prev, days: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <MDButton
            variant="text"
            color="dark"
            onClick={() => setAdjustDialog({ open: false, id: "", days: "30" })}
          >
            Close
          </MDButton>
          <MDButton
            variant="gradient"
            color="info"
            sx={{
              background: "rgba(229, 10, 70, 1)",
              "&:hover": { background: "rgba(229, 10, 70, 0.9)" },
            }}
            onClick={handleConfirmAdjust}
          >
            Update
          </MDButton>
        </DialogActions>
      </Dialog>
      <Dialog
        open={editDialog.open}
        onClose={() =>
          setEditDialog({
            open: false,
            id: "",
            nextBilling: "",
            durationAction: "none",
            paused: false,
            autoConvert: false,
          })
        }
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle
          sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
        >
          Edit Subscription
          <IconButton
            size="small"
            onClick={() =>
              setEditDialog({
                open: false,
                id: "",
                nextBilling: "",
                durationAction: "none",
                paused: false,
                autoConvert: false,
              })
            }
            sx={{ color: "#344767" }}
          >
            <Icon fontSize="small">close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <MDBox
            mt={0.5}
            p={2}
            borderRadius="lg"
            sx={{ backgroundColor: "rgba(52, 71, 103, 0.04)" }}
          >
            <MDTypography variant="caption" fontWeight="bold" color="text">
              Next Billing Date
            </MDTypography>
            <TextField
              type="date"
              fullWidth
              size="small"
              value={editDialog.nextBilling}
              onChange={(e) => setEditDialog((prev) => ({ ...prev, nextBilling: e.target.value }))}
              sx={{ mt: 0.75, backgroundColor: "#fff" }}
              InputLabelProps={{ shrink: true }}
            />
          </MDBox>
          <MDBox
            mt={1.5}
            p={2}
            borderRadius="lg"
            sx={{ backgroundColor: "rgba(52, 71, 103, 0.04)" }}
          >
            <MDTypography variant="caption" fontWeight="bold" color="text">
              Subscription Adjustment
            </MDTypography>
            <TextField
              select
              fullWidth
              size="small"
              value={editDialog.durationAction}
              onChange={(e) =>
                setEditDialog((prev) => ({ ...prev, durationAction: e.target.value }))
              }
              sx={{
                mt: 0.75,
                backgroundColor: "#fff",
                "& .MuiOutlinedInput-root": { minHeight: "40px" },
              }}
            >
              <MenuItem value="none">No change</MenuItem>
              <MenuItem value="extend_7">Extend +7 days</MenuItem>
              <MenuItem value="extend_30">Extend +30 days</MenuItem>
            </TextField>
          </MDBox>
          <MDBox
            mt={1.5}
            p={2}
            borderRadius="lg"
            sx={{ backgroundColor: "rgba(52, 71, 103, 0.04)" }}
          >
            <MDBox display="flex" flexDirection="column" gap={0.25}>
              <FormControlLabel
                sx={{ m: 0 }}
                control={
                  <Switch
                    checked={!editDialog.paused}
                    onChange={(e) =>
                      setEditDialog((prev) => ({ ...prev, paused: !e.target.checked }))
                    }
                  />
                }
                label="Subscription active"
              />
              <FormControlLabel
                sx={{ m: 0 }}
                control={
                  <Switch
                    checked={editDialog.autoConvert}
                    onChange={(e) =>
                      setEditDialog((prev) => ({ ...prev, autoConvert: e.target.checked }))
                    }
                  />
                }
                label="Auto-convert trial to paid"
              />
            </MDBox>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton
            variant="text"
            color="dark"
            onClick={() =>
              setEditDialog({
                open: false,
                id: "",
                nextBilling: "",
                durationAction: "none",
                paused: false,
                autoConvert: false,
              })
            }
          >
            Cancel
          </MDButton>
          <MDButton
            variant="gradient"
            color="info"
            sx={{
              background: "rgba(229, 10, 70, 1)",
              "&:hover": { background: "rgba(229, 10, 70, 0.9)" },
            }}
            onClick={handleSaveEdit}
          >
            Save
          </MDButton>
        </DialogActions>
      </Dialog>
      <ToastContainer position="top-right" autoClose={3000} />
    </DashboardLayout>
  );
}

export default Subscriptions;
